import Axios, { AxiosRequestConfig } from 'axios';

export const AXIOS_INSTANCE = Axios.create({
  baseURL: '',
  withCredentials: true,
});

// Add request interceptor for authentication
AXIOS_INSTANCE.interceptors.request.use(
  (config) => {
    // Session-based auth (cookies) is handled by the browser.
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
AXIOS_INSTANCE.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth-storage');
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.replace('/login');
      }
    }
    return Promise.reject(error);
  }
);

type ApiResult<T> = T extends object ? T & { data: T } : { data: T };

export const customInstance = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig,
): Promise<ApiResult<T>> => {
  const source = Axios.CancelToken.source();
  const promise = AXIOS_INSTANCE<T>({
    ...config,
    ...options,
    cancelToken: source.token,
  }).then(({ data }) => {
    if (Array.isArray(data)) {
      const wrapped = [...data] as unknown as ApiResult<T>;
      Object.defineProperty(wrapped, 'data', {
        value: data,
        enumerable: true,
        configurable: true,
      });
      return wrapped;
    }

    if (data !== null && typeof data === 'object') {
      const wrapped = { ...(data as Record<string, unknown>) } as ApiResult<T>;
      Object.defineProperty(wrapped, 'data', {
        value: data,
        enumerable: true,
        configurable: true,
      });
      return wrapped;
    }

    return { data } as ApiResult<T>;
  });

  // @ts-ignore
  promise.cancel = () => {
    source.cancel('Query was cancelled');
  };

  return promise;
};

export default customInstance;
